import { useLiveQuery } from 'dexie-react-hooks';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { EntityName } from '@dairy/shared';
import { toast } from '@/components/toast';
import { Button, Card, EmptyState, PageHeader } from '@/components/ui';
import { db, type OutboxEntry } from '@/db/db';
import { discardRejected } from '@/db/repo';
import { apiJson, ApiError } from '@/lib/api';
import { useFormat } from '@/lib/format';
import { syncNow, useSyncStatus } from '@/sync/client';

/** Where "Edit and retry" opens the record's form. */
function editPath(o: OutboxEntry): string | null {
  const d = (o.data ?? {}) as Record<string, string>;
  const map: Partial<Record<EntityName, string>> = {
    animal: `/animals/${o.recordId}/edit`,
    event: `/events/${o.recordId}/edit`,
    milkRecord: `/milk/session?date=${d.date}&session=${d.session}`,
    dailyMilkUsage: `/milk/usage?date=${d.date}`,
    reminder: '/reminders',
    healthProtocol: '/settings/protocols',
    buyer: `/buyers/${o.recordId}/edit`,
    sale: `/sales/${o.recordId}/edit`,
    payment: `/payments/${o.recordId}/edit`,
    expenseCategory: '/settings/categories',
    expense: `/expenses/${o.recordId}/edit`,
    recurringExpense: '/expenses/recurring',
  };
  return o.op === 'delete' ? null : (map[o.entity] ?? null);
}

/** Screen 28 — Sync status and Sync Issues (PL-9): rejected changes never disappear silently. */
export function SyncIssuesPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const navigate = useNavigate();
  const status = useSyncStatus();
  const rejected = useLiveQuery(() => db.outbox.where('status').equals('rejected').toArray(), []) ?? [];

  /** Discard: delete the outbox entry and restore the server's version (or remove the local row on 404). */
  const discard = async (o: OutboxEntry) => {
    try {
      await discardRejected(o, await apiJson<Record<string, unknown>>(`/sync/record/${o.entity}/${o.recordId}`));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        await discardRejected(o, null);
      } else {
        toast(t('reports.needsInternet'), 'error');
        return;
      }
    }
    toast(t('sync.discarded'));
  };

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader title={t('sync.issuesTitle')} />
      <Card className="space-y-2">
        <div className="flex justify-between">
          <span>{t('sync.lastSync')}</span>
          <span className="font-semibold">{status.lastSyncAt ? `${f.date(status.lastSyncAt.slice(0, 10))} ${f.time(status.lastSyncAt)}` : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span>{t('sync.pendingLabel')}</span>
          <span className="font-semibold">{status.pending}</span>
        </div>
        <Button className="w-full" onClick={() => void syncNow()} disabled={status.state === 'syncing'}>
          <RefreshCw className={`h-4 w-4 ${status.state === 'syncing' ? 'animate-spin' : ''}`} /> {t('sync.syncNow')}
        </Button>
      </Card>
      {rejected.length === 0 ? <EmptyState>{t('sync.noIssues')}</EmptyState> : null}
      <ul className="space-y-2">
        {rejected.map((o) => {
          const path = editPath(o);
          return (
            <li key={o.seq}>
              <Card className="space-y-2 p-3" data-testid="sync-issue">
                <div className="font-semibold">
                  {t(`sync.entity.${o.entity}`)} · {t(`sync.op.${o.op}`)}
                </div>
                <div className="text-red-800">{t(`errors.${o.error?.code ?? 'UNKNOWN'}`, { defaultValue: t('errors.UNKNOWN') })}</div>
                <div className="text-sm text-stone-500">{f.date(o.clientTs.slice(0, 10))}</div>
                <div className="flex gap-2">
                  {path ? (
                    <Button size="sm" onClick={() => navigate(path)}>
                      {t('sync.editRetry')}
                    </Button>
                  ) : null}
                  <Button size="sm" variant="secondary" onClick={() => void discard(o)}>
                    {t('sync.discard')}
                  </Button>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
